// Copyright The Linux Foundation and each contributor to LFX.
// SPDX-License-Identifier: MIT
import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { map, catchError, switchMap } from 'rxjs/operators';
import { environment } from '@environment';
import { Column, DataRow } from '@lfx/shared/interfaces';
import { CompanyService } from '@lfx/core/services';
import {
  MetricChartEntityService,
  MetricChartData,
  MetricChartApiResponse,
} from './metric-chart-base.entity';
import { formatDataSources } from './metric-column-formatters.utility';

/**
 * Maintainer data model from SQL query (API returns uppercase keys)
 */
export interface MaintainerData {
  MEMBER_ID: string;
  MEMBER_DISPLAY_NAME: string;
  MEMBER_LOGO_URL: string;
  PLATFORM: string;
  PLATFORM_VALUE: string;
  START_DATE: string;
}

/**
 * Maintainer summary data model from SQL query
 */
export interface MaintainerSummaryData {
  totalMaintainers: number;
  dataSources: string;
}

/**
 * Entity service for maintainers metric type
 * Handles SQL query: analytics.gold_organization_dashboard.maintainers
 */
@Injectable({
  providedIn: 'root',
})
export class MaintainersMetricEntity implements MetricChartEntityService {
  private readonly http = inject(HttpClient);
  private readonly companyService = inject(CompanyService);

  /**
   * Get columns for maintainers table
   */
  getColumns(): Column[] {
    return [
      // { name: 'Member ID', prop: 'memberId' },
      { name: 'Our Contributors', prop: 'displayName', clickableText: true },
      // { name: 'Platform', prop: 'platform' },
      { name: 'Username', prop: 'platformValue' },
      { name: 'Granted Maintainer Status', prop: 'startDate' },
    ];
  }

  /**
   * Get API endpoint for maintainers
   */
  getEndpoint(): string {
    return `${environment.apiUrl}/maintainers`;
  }

  /**
   * Fetch maintainers data from backend
   * Executes SQL: select member_id, member_display_name, platform, platform_value, start_date
   * from analytics.gold_organization_dashboard.maintainers
   * where account_id='{account_id}' and project_slug='{project_slug}'
   * and role='maintainer' and time_range_name='last_24mo'
   * order by start_date desc
   */
  fetchData(params: MetricChartData): Observable<DataRow[]> {
    return this.companyService.getMyCompany().pipe(
      switchMap(company => {
        if (!company?.salesforceB2BAccountId) {
          throw new Error('No organization ID found');
        }

        const httpParams = new HttpParams()
          .set('accountId', company.salesforceB2BAccountId)
          .set('projectSlug', params.projectSlug || '')
          .set('timeRangeName', params.timeRangeName || 'last_24mo');

        return this.http
          .get<
            MetricChartApiResponse<MaintainerData>
          >(this.getEndpoint(), { params: httpParams })
          .pipe(
            map(response => {
              if (!response.success) {
                throw new Error(
                  response.message || 'Failed to fetch maintainers data'
                );
              }

              return response.data.map(maintainer =>
                this.transformToDataRow(maintainer)
              );
            }),
            catchError(error => {
              console.error('Error fetching maintainers data:', error);
              return of([]);
            })
          );
      }),
      catchError(error => {
        console.error('Error in maintainers entity service:', error);
        return of([]);
      })
    );
  }

  /**
   * Transform maintainer data to DataRow format for the table
   */
  private transformToDataRow(maintainer: MaintainerData): DataRow {
    const platformValue = this.formatPlatformValue(maintainer.PLATFORM, maintainer.PLATFORM_VALUE);
    const displayNameWithImage = this.formatDisplayNameWithImage(
      maintainer.MEMBER_DISPLAY_NAME,
      maintainer.MEMBER_LOGO_URL
    );

    return {
      memberId: { text: maintainer.MEMBER_ID },
      displayName: {
        ...displayNameWithImage,
        extraData: {
          memberId: maintainer.MEMBER_ID,
          memberDisplayName: maintainer.MEMBER_DISPLAY_NAME,
          memberLogoUrl: maintainer.MEMBER_LOGO_URL
        }
      },
      platform: { text: maintainer.PLATFORM },
      platformValue: platformValue,
      startDate: { text: this.formatDate(maintainer.START_DATE) },
    };
  }

  /**
   * Format display name with circular image
   */
  private formatDisplayNameWithImage(
    displayName: string,
    logoUrl: string
  ): { text: string; isHtml: boolean } {
    const imageHtml = logoUrl
      ? `<img src="${logoUrl}" alt="${displayName}" class="maintainer-avatar" onerror="this.style.display='none'">`
      : '';

    return {
      text: `<div class="maintainer-profile">${imageHtml}<span class="maintainer-name">${displayName}</span></div>`,
      isHtml: true
    };
  }

  /**
   * Format platform value with appropriate prefix and icons
   */
  private formatPlatformValue(platform: string, platformValue: string): { text: string; isHtml?: boolean } {
    if (platform.toLowerCase() === 'github') {
      return {
        text: `<i class="fab fa-github" style="margin-right: 4px;"></i> &nbsp;@${platformValue}`,
        isHtml: true
      };
    }

    return { text: platformValue };
  }

  /**
   * Fetch maintainers summary data from backend
   * Executes SQL: select count(distinct member_id) total_maintainers, listagg(distinct platform, ',') data_sources
   * from analytics.gold_organization_dashboard.maintainers
   * where project_slug='{project_slug}' and time_range_name='last_24mo'
   */
  getSummaryData(
    params: MetricChartData
  ): Observable<{ columns: Column[]; data: DataRow[] }> {
    return this.companyService.getMyCompany().pipe(
      switchMap(company => {
        if (!company?.salesforceB2BAccountId) {
          throw new Error('No organization ID found');
        }

        const httpParams = new HttpParams()
          .set('projectSlug', params.projectSlug || '')
          .set('timeRangeName', params.timeRangeName || 'last_24mo');

        return this.http
          .get<{
            success: boolean;
            message?: string;
            data: MaintainerSummaryData;
          }>(`${this.getEndpoint()}/summary`, { params: httpParams })
          .pipe(
            map(response => {
              if (!response.success) {
                throw new Error(
                  response.message || 'Failed to fetch maintainers summary'
                );
              }

              // Define columns for summary table
              const summaryColumns: Column[] = [
                { name: 'Definition', prop: 'definition' },
                { name: 'Total count for this project', prop: 'totalCount' },
                { name: 'Data source', prop: 'dataSource' },
              ];

              // Transform API response to summary table format
              const summaryData: DataRow[] = [
                {
                  definition: {
                    text: `View definition of a maintainer and how it is calculated in <a href="https://insights.linuxfoundation.org/docs/introduction/maintainers/" target="_blank" rel="noopener noreferrer">LFX Insights</a>.`,
                    isHtml: true,
                  },
                  totalCount: {
                    text: response.data.totalMaintainers.toLocaleString('en-US'),
                  },
                  dataSource: { text: formatDataSources(response.data.dataSources) },
                },
              ];

              return { columns: summaryColumns, data: summaryData };
            }),
            catchError(error => {
              console.error('Error fetching maintainers summary:', error);
              // Return fallback data on error
              const fallbackColumns: Column[] = [
                { name: 'Definition', prop: 'definition' },
                { name: 'Total count for this project', prop: 'totalCount' },
                { name: 'Data source', prop: 'dataSource' },
              ];
              const fallbackData: DataRow[] = [
                {
                  definition: {
                    text: `View definition of a maintainer and how it is calculated in <a href="https://insights.linuxfoundation.org/docs/introduction/maintainers/" target="_blank" rel="noopener noreferrer">LFX Insights</a>.`,
                    isHtml: true,
                  },
                  totalCount: { text: 'N/A' },
                  dataSource: { text: 'N/A' },
                },
              ];
              return of({ columns: fallbackColumns, data: fallbackData });
            })
          );
      }),
      catchError(error => {
        console.error('Error in maintainers summary entity service:', error);
        // Return fallback data
        const fallbackColumns: Column[] = [
          { name: 'Definition', prop: 'definition' },
          { name: 'Total count for this project', prop: 'totalCount' },
          { name: 'Data source', prop: 'dataSource' },
        ];
        const fallbackData: DataRow[] = [
          {
            definition: {
              text: `View definition of a maintainer and how it is calculated in <a href="https://insights.linuxfoundation.org/docs/introduction/maintainers/" target="_blank" rel="noopener noreferrer">LFX Insights</a>.`,
              isHtml: true,
            },
            totalCount: { text: 'N/A' },
            dataSource: { text: 'N/A' },
          },
        ];
        return of({ columns: fallbackColumns, data: fallbackData });
      })
    );
  }

  /**
   * Format date string for display
   */
  private formatDate(dateString: string): string {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    } catch {
      return dateString; // Return original if parsing fails
    }
  }
}
